import React, { useEffect, useState } from 'react';
import { supabase } from '../../../supabaseClient';
import speakersDataRaw from '../../../data/speakers.json';
import { sanitizeStrictText, sanitizeText, isValidURL, validateLength } from '../../../utils/sanitize';

export default function SpeakersTab() {
    const [speakers, setSpeakers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    
    // Form state
    const [formData, setFormData] = useState({
        id: null, name: '', role: '', org: '', track: 'Offensive', 
        talk: '', bio: '', img: '', twitter: '', linkedin: '', keynote: false
    });

    const tracks = ["All", "Offensive", "Defensive", "CTF", "Cloud & AppSec", "AI / ML", "Training"];

    useEffect(() => {
        fetchSpeakers();
    }, []);

    const fetchSpeakers = async () => {
        setLoading(true);
        const { data, error } = await supabase
            .from('speakers')
            .select('*')
            .order('name', { ascending: true });

        if (error) {
            setError(error.message);
        } else {
            setSpeakers(data || []);
        }
        setLoading(false);
    };

    const handleImportJson = async () => {
        if (!window.confirm("This will import speakers from speakers.json. Are you sure?")) return;
        setLoading(true);
        
        try {
            const inserts = speakersDataRaw.speakers.map(s => ({
                name: s.name, role: s.role, org: s.org, track: s.track,
                talk: s.talk, bio: s.bio, img: s.img, twitter: s.twitter,
                linkedin: s.linkedin, keynote: s.keynote
            }));
            
            const { error } = await supabase.from('speakers').insert(inserts);
            if (error) throw error;
            
            alert('Import successful!');
            fetchSpeakers();
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this speaker?")) return;
        setLoading(true);
        const { error } = await supabase.from('speakers').delete().eq('id', id);
        if (error) setError(error.message);
        fetchSpeakers();
    };

    const handleEdit = (speaker) => {
        setFormData(speaker);
        setIsEditing(true);
        setUploadSuccess(false);
    };

    const handleAddNew = () => {
        setFormData({
            id: null, name: '', role: '', org: '', track: 'Offensive', 
            talk: '', bio: '', img: '', twitter: '', linkedin: '', keynote: false
        });
        setIsEditing(true);
        setUploadSuccess(false);
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setUploadSuccess(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        // ── Input validation ──
        const nameCheck = validateLength(formData.name, 100, 2);
        if (!nameCheck.valid) { setError(`Name: ${nameCheck.reason}`); setLoading(false); return; }

        if (formData.twitter && !isValidURL(formData.twitter)) {
            setError('Twitter URL must be a valid https:// link.'); setLoading(false); return;
        }
        if (formData.linkedin && !isValidURL(formData.linkedin)) {
            setError('LinkedIn URL must be a valid https:// link.'); setLoading(false); return;
        }

        const bioCheck = validateLength(formData.bio || '', 2000, 0);
        if (!bioCheck.valid) { setError(`Bio: ${bioCheck.reason}`); setLoading(false); return; }

        const talkCheck = validateLength(formData.talk || '', 300, 0);
        if (!talkCheck.valid) { setError(`Talk title: ${talkCheck.reason}`); setLoading(false); return; }

        // ── Sanitize before saving ──
        const payload = {
            name: sanitizeStrictText(formData.name, 100),
            role: sanitizeStrictText(formData.role || '', 100),
            org: sanitizeStrictText(formData.org || '', 100),
            track: formData.track,
            talk: sanitizeText(formData.talk || '').slice(0, 300),
            bio: sanitizeText(formData.bio || '').slice(0, 2000),
            img: formData.img || '',
            twitter: (formData.twitter || '').trim().slice(0, 2048),
            linkedin: (formData.linkedin || '').trim().slice(0, 2048),
            keynote: !!formData.keynote,
        };
        
        if (formData.id) {
            // Update
            const { error: updateError } = await supabase.from('speakers').update(payload).eq('id', formData.id);
            if (updateError) setError(updateError.message);
        } else {
            // Insert
            const { error: insertError } = await supabase.from('speakers').insert([payload]);
            if (insertError) setError(insertError.message);
        }
        
        if (!error) {
            setIsEditing(false);
            fetchSpeakers();
        } else {
            setLoading(false);
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // ── File type validation ──
        const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/svg+xml'];
        const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'gif', 'svg'];

        const fileExt = file.name.split('.').pop().toLowerCase();

        if (!ALLOWED_MIME_TYPES.includes(file.type)) {
            setError(`Invalid file type "${file.type}". Allowed: JPEG, PNG, WebP, GIF, SVG.`);
            e.target.value = '';
            return;
        }

        if (!ALLOWED_EXTENSIONS.includes(fileExt)) {
            setError(`Invalid file extension ".${fileExt}". Allowed: ${ALLOWED_EXTENSIONS.join(', ')}.`);
            e.target.value = '';
            return;
        }

        // ── Size validation ──
        if (file.size > 500 * 1024) {
            setError('Image must be under 500KB.');
            e.target.value = '';
            return;
        }

        // ── Filename sanitization (strip path traversal / special chars) ──
        const safeName = `${Math.random().toString(36).substring(2, 15)}_${Date.now()}.${fileExt}`;

        setUploading(true);
        setError(null);
        setUploadSuccess(false);

        try {
            const { error: uploadError } = await supabase.storage
                .from('speaker-images')
                .upload(safeName, file, {
                    contentType: file.type,
                    upsert: false,
                });

            if (uploadError) throw uploadError;

            const { data } = supabase.storage
                .from('speaker-images')
                .getPublicUrl(safeName);

            setFormData({ ...formData, img: data.publicUrl });
            setUploadSuccess(true);
        } catch (err) {
            console.error("Upload error:", err);
            setError(err.message || 'Failed to upload image');
        } finally {
            setUploading(false);
        }
    };

    if (isEditing) {
        return (
            <div className="card shadow-lg border-0 rounded-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="card-header border-0 bg-transparent py-4 px-4">
                    <h4 className="text-white m-0" style={{ fontFamily: 'var(--eventflow-font)', fontWeight: 700 }}>
                        {formData.id ? 'Edit Speaker' : 'Add New Speaker'}
                    </h4>
                </div>
                <div className="card-body px-4 pb-4 text-white">
                    {error && <div className="alert alert-danger">{error}</div>}
                    <form onSubmit={handleSubmit}>
                        <div className="row g-3">
                            <div className="col-md-6">
                                <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Name</label>
                                <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Image (Max 500KB, Rec: 410x455px)</label>
                                <div className="d-flex align-items-center gap-2">
                                    <input type="file" accept=".jpg,.jpeg,.png,.webp,.gif,.svg" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} onChange={handleImageUpload} disabled={uploading} />
                                    {uploading && <div className="spinner-border spinner-border-sm text-light" role="status"></div>}
                                </div>
                                {uploadSuccess && (
                                    <div className="mt-2 text-success" style={{ fontSize: '12px' }}>
                                        <i className="fas fa-check-circle me-1"></i> Image uploaded successfully
                                    </div>
                                )}
                                {formData.img && !uploadSuccess && (
                                    <div className="mt-2" style={{ fontSize: '12px' }}>
                                        <a href={formData.img} target="_blank" rel="noreferrer" style={{ color: 'var(--eventflow-primary)', textDecoration: 'none' }}>
                                            View current image <i className="fas fa-external-link-alt ms-1"></i>
                                        </a>
                                    </div>
                                )}
                            </div>
                            <div className="col-md-4">
                                <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Role</label>
                                <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.role} onChange={e => setFormData({...formData, role: e.target.value})} />
                            </div>
                            <div className="col-md-4">
                                <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Organization</label>
                                <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.org} onChange={e => setFormData({...formData, org: e.target.value})} />
                            </div>
                            <div className="col-md-4">
                                <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Track</label>
                                <select className="form-select bg-dark text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.track} onChange={e => setFormData({...formData, track: e.target.value})}>
                                    {tracks.filter(t => t !== 'All').map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>
                            <div className="col-12">
                                <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Talk Title</label>
                                <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.talk} onChange={e => setFormData({...formData, talk: e.target.value})} />
                            </div>
                            <div className="col-12">
                                <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Bio</label>
                                <textarea className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} rows="3" value={formData.bio} onChange={e => setFormData({...formData, bio: e.target.value})}></textarea>
                            </div>
                            <div className="col-md-5">
                                <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Twitter URL</label>
                                <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.twitter} onChange={e => setFormData({...formData, twitter: e.target.value})} />
                            </div>
                            <div className="col-md-5">
                                <label className="form-label" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>LinkedIn URL</label>
                                <input type="text" className="form-control bg-transparent text-white" style={{ border: '1px solid rgba(255,255,255,0.2)' }} value={formData.linkedin} onChange={e => setFormData({...formData, linkedin: e.target.value})} />
                            </div>
                            <div className="col-md-2 d-flex align-items-end">
                                <div className="form-check mb-2">
                                    <input className="form-check-input" type="checkbox" id="keynoteCheck" checked={formData.keynote} onChange={e => setFormData({...formData, keynote: e.target.checked})} />
                                    <label className="form-check-label" htmlFor="keynoteCheck">Keynote</label>
                                </div>
                            </div>
                            
                            <div className="col-12 mt-4 d-flex gap-2">
                                <button type="submit" className="btn text-white fw-bold" style={{ background: 'var(--eventflow-primary)' }} disabled={loading}>
                                    {loading ? 'Saving...' : 'Save Speaker'}
                                </button>
                                <button type="button" className="btn btn-outline-light" onClick={handleCancelEdit} disabled={loading}>Cancel</button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <>
            {error && <div className="alert alert-danger">{error}</div>}
            
            <div className="card shadow-lg border-0 rounded-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="card-header border-0 bg-transparent py-4 px-4 d-flex justify-content-between align-items-center flex-wrap gap-3">
                    <h4 className="text-white m-0" style={{ fontFamily: 'var(--eventflow-font)', fontWeight: 700 }}>Speakers</h4>
                    <div className="d-flex gap-2">
                        {speakers.length === 0 && (
                            <button onClick={handleImportJson} className="btn btn-sm btn-outline-warning">
                                <i className="fa fa-download me-2"></i>Import from JSON
                            </button>
                        )}
                        <button onClick={fetchSpeakers} className="btn btn-sm text-white" style={{ background: 'rgba(255,255,255,0.1)' }}>
                            <i className="fa fa-refresh me-2"></i>Refresh
                        </button>
                        <button onClick={handleAddNew} className="btn btn-sm text-white fw-bold" style={{ background: 'var(--eventflow-primary)' }}>
                            <i className="fa fa-plus me-2"></i>Add Speaker
                        </button>
                    </div>
                </div>
                
                <div className="card-body p-0">
                    {loading ? (
                        <div className="text-center py-5 text-white">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : speakers.length === 0 ? (
                        <div className="text-center py-5" style={{ color: 'rgba(255,255,255,0.5)' }}>
                            No speakers found. Try importing from JSON.
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-dark table-hover mb-0" style={{ background: 'transparent' }}>
                                <thead style={{ background: 'rgba(255,255,255,0.05)' }}>
                                    <tr>
                                        <th className="px-4 py-3 border-0">Name</th>
                                        <th className="px-4 py-3 border-0">Track</th>
                                        <th className="px-4 py-3 border-0">Role</th>
                                        <th className="px-4 py-3 border-0 text-center">Keynote</th>
                                        <th className="px-4 py-3 border-0 text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {speakers.map((s) => (
                                        <tr key={s.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                            <td className="px-4 py-3 text-white fw-bold" style={{ verticalAlign: 'middle' }}>{s.name}</td>
                                            <td className="px-4 py-3 text-white" style={{ verticalAlign: 'middle' }}>
                                                <span className="badge" style={{ background: 'rgba(255,255,255,0.1)' }}>{s.track}</span>
                                            </td>
                                            <td className="px-4 py-3 text-white" style={{ verticalAlign: 'middle', color: 'rgba(255,255,255,0.7)' }}>
                                                {s.role}
                                            </td>
                                            <td className="px-4 py-3 text-center" style={{ verticalAlign: 'middle' }}>
                                                {s.keynote ? <span className="badge bg-warning text-dark">Keynote</span> : '-'}
                                            </td>
                                            <td className="px-4 py-3 text-end" style={{ verticalAlign: 'middle' }}>
                                                <button onClick={() => handleEdit(s)} className="btn btn-sm btn-outline-light me-2"><i className="fas fa-edit"></i></button>
                                                <button onClick={() => handleDelete(s.id)} className="btn btn-sm btn-outline-danger"><i className="fas fa-trash-alt"></i></button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
